/**
 * Rich Text Display Component
 * 
 * Safely renders HTML content from the rich text editor.
 * Uses DOMPurify to sanitize HTML and prevent XSS attacks.
 */

import DOMPurify from 'dompurify';
import { cn } from '@/lib/utils';

interface RichTextDisplayProps {
  content: string;
  className?: string;
  /**
   * Maximum content width for prose styling
   * @default 'max-w-none'
   */
  prose?: boolean;
}

export const RichTextDisplay = ({
  content,
  className,
  prose = true,
}: RichTextDisplayProps) => {
  if (!content) {
    return null;
  }

  // Sanitize HTML content to prevent XSS attacks
  const sanitizedContent = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'strike',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'a', 'img',
      'div', 'span',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'class', 'style'],
    ALLOW_DATA_ATTR: false,
  });

  return (
    <div
      className={cn(
        prose && 'prose prose-sm sm:prose lg:prose-lg max-w-none',
        'rich-text-content',
        className
      )}
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
    />
  );
};


